import { useState, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  NodeTypes,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  RiCloseLine,
  RiFileCopyLine,
  RiEyeLine,
  RiHeartLine,
  RiLayoutGridLine,
  RiLinksLine,
  RiCalendarLine,
  RiUserLine,
  RiLoader4Line,
  RiTableLine,
} from '@remixicon/react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { TableNodeComponent } from '@/components/visual-schema/table-node';
import { formatDistanceToNow } from 'date-fns';

const nodeTypes: NodeTypes = {
  tableNode: TableNodeComponent,
};

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: 'hsl(var(--primary))',
    width: 20,
    height: 20,
  },
};

interface TemplatePreviewModalProps {
  templateId: string | null;
  open: boolean;
  onClose: () => void;
  onDuplicate?: () => void;
}

export function TemplatePreviewModal({
  templateId,
  open,
  onClose,
  onDuplicate,
}: TemplatePreviewModalProps) {
  const [template, setTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    if (templateId && open) {
      fetchTemplate();
    }
  }, [templateId, open]);

  const fetchTemplate = async () => {
    if (!templateId) return;

    setLoading(true);
    try {
      const data = await api.getTemplateById(templateId);
      setTemplate(data);

      // Increment view count
      await api.incrementTemplateView(templateId);
    } catch (error: any) {
      console.error('Failed to fetch template:', error);
      toast.error('Failed to load template preview');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!templateId) return;

    setDuplicating(true);
    try {
      await api.duplicateTemplate(templateId);
      toast.success('Template duplicated to your workspace!');
      onClose();
      if (onDuplicate) {
        onDuplicate();
      }
    } catch (error: any) {
      console.error('Failed to duplicate template:', error);
      toast.error(error.message || 'Failed to duplicate template');
    } finally {
      setDuplicating(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] p-0 gap-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <RiLoader4Line className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : template ? (
          <div className="flex h-full">
            {/* Left Side - Schema Visualization */}
            <div className="flex-1 flex flex-col border-r">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <DialogHeader className="space-y-0">
                  <DialogTitle className="text-xl">{template.template.title}</DialogTitle>
                  <DialogDescription className="mt-1">
                    {template.template.description || 'No description provided'}
                  </DialogDescription>
                </DialogHeader>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="flex-shrink-0"
                >
                  <RiCloseLine className="h-5 w-5" />
                </Button>
              </div>

              {/* ReactFlow Canvas */}
              <div className="flex-1 bg-muted/30">
                <ReactFlow
                  nodes={template.schema.nodes}
                  edges={template.schema.edges}
                  nodeTypes={nodeTypes}
                  defaultEdgeOptions={defaultEdgeOptions}
                  fitView
                  minZoom={0.1}
                  maxZoom={2}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  elementsSelectable={false}
                  className="bg-muted/20"
                >
                  <Background gap={20} size={1} className="bg-muted/10" />
                  <Controls
                    showInteractive={false}
                    className="bg-background border border-border shadow-lg rounded-lg"
                  />
                </ReactFlow>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between px-6 py-4 border-t bg-background">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <RiLayoutGridLine className="h-4 w-4" />
                    <span>{template.schema.nodes.length} tables</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RiLinksLine className="h-4 w-4" />
                    <span>{template.schema.edges.length} relationships</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RiEyeLine className="h-4 w-4" />
                    <span>{template.template.viewCount} views</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RiFileCopyLine className="h-4 w-4" />
                    <span>{template.template.duplicateCount} duplicates</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                  <Button
                    onClick={handleDuplicate}
                    disabled={duplicating}
                    className="gap-2"
                  >
                    {duplicating ? (
                      <>
                        <RiLoader4Line className="h-4 w-4 animate-spin" />
                        Duplicating...
                      </>
                    ) : (
                      <>
                        <RiFileCopyLine className="h-4 w-4" />
                        Duplicate to Workspace
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Side - Details */}
            <div className="w-[400px] flex flex-col overflow-hidden">
              {/* Metadata */}
              <div className="p-6 space-y-4 border-b">
                {/* Categories */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {template.template.categories.map((category: string) => (
                      <Badge key={category} variant="secondary">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Author & Date */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <RiUserLine className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Created by</span>
                    <span className="font-medium">
                      {template.user.name || 'Anonymous'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <RiCalendarLine className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Published</span>
                    <span className="font-medium">
                      {formatDistanceToNow(new Date(template.template.publishedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tables Breakdown */}
              <div className="flex-1 overflow-y-auto p-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <h4 className="text-sm font-medium mb-3">Tables & Columns</h4>
                <Accordion type="single" collapsible className="w-full">
                  {template.schema.nodes.map((node: any, index: number) => (
                    <AccordionItem key={node.id} value={node.id}>
                      <AccordionTrigger className="text-sm hover:no-underline">
                        <div className="flex items-center gap-2">
                          <RiTableLine className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{node.data.name}</span>
                          <Badge variant="outline" className="ml-auto mr-2 text-xs">
                            {node.data.columns.length} columns
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-1 pl-6">
                          {node.data.columns.map((column: any) => (
                            <div
                              key={column.id}
                              className="flex items-center justify-between py-1.5 text-xs"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-mono">{column.name}</span>
                                {column.primaryKey && (
                                  <Badge variant="default" className="text-xs px-1 py-0">
                                    PK
                                  </Badge>
                                )}
                              </div>
                              <span className="text-muted-foreground font-mono">
                                {column.type}
                              </span>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
